import { useCallback, useEffect, useState } from "react";
import { Save, Send, Trash2, UserRound, X } from "lucide-react";
import API from "../../services/api";
import "./HREmployeeDetailsPage.css";

const emptyEmployeeDetails = {
  employeeId: "",
  employeeName: "",
  employeeEmail: "",
  employeeMobileNumber: "",
};

const recordsPerPage = 10;

const getEmployeePath = (employeeId) =>
  `/referral/employee/${encodeURIComponent(employeeId)}`;

const HREmployeeDetailsPage = () => {
  const [employeeDetails, setEmployeeDetails] = useState(emptyEmployeeDetails);
  const [submittedDetails, setSubmittedDetails] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modalEmployeeDetails, setModalEmployeeDetails] = useState(emptyEmployeeDetails);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchEmployeeDetails = useCallback(async (page = currentPage) => {
    try {
      const response = await API.get("/referral", {
        params: {
          page,
          limit: recordsPerPage,
        },
      });

      setSubmittedDetails(response.data?.data || []);
      setTotalPages(response.data?.meta?.totalPages || 1);
    } catch (error) {
      console.error(
        "Failed to fetch employee detalis",
        error.response?.data || error.message,
      );
      setSubmittedDetails([]);
      setTotalPages(1);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchEmployeeDetails(currentPage);
  }, [currentPage, fetchEmployeeDetails]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setEmployeeDetails((currentDetails) => ({
      ...currentDetails,
      [name]: value,
    }));
  };

  const handleModalChange = (event) => {
    const { name, value } = event.target;
    setModalEmployeeDetails((currentDetails) => ({
      ...currentDetails,
      [name]: value,
    }));
  };

  const handleEmployeeClick = async (employee) => {
    setSelectedEmployee(employee);
    setModalEmployeeDetails({
      employeeId: employee.employeeId || "",
      employeeName: employee.employeeName || "",
      employeeEmail: employee.employeeEmail || "",
      employeeMobileNumber: employee.employeeMobileNumber || "",
    });

    try {
      const response = await API.get(getEmployeePath(employee.employeeId));
      const employeeData = response.data?.data || response.data || employee;

      setSelectedEmployee(employeeData);
      setModalEmployeeDetails({
        employeeId: employeeData.employeeId || "",
        employeeName: employeeData.employeeName || "",
        employeeEmail: employeeData.employeeEmail || "",
        employeeMobileNumber: employeeData.employeeMobileNumber || "",
      });
    } catch (error) {
      console.error(
        "Failed to fetch employee detalis",
        error.response?.data || error.message,
      );
    }
  };

  const handleClosePopup = () => {
    setSelectedEmployee(null);
    setModalEmployeeDetails(emptyEmployeeDetails);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await API.post("/referral/employee", employeeDetails);
      const savedEmployee =
        response.data?.data ||
        response.data?.employee ||
        response.data ||
        employeeDetails;

      const normalizedEmployee = {
        employeeId: savedEmployee.employeeId || employeeDetails.employeeId,
        employeeName: savedEmployee.employeeName || employeeDetails.employeeName,
        employeeEmail: savedEmployee.employeeEmail || employeeDetails.employeeEmail,
        employeeMobileNumber:
          savedEmployee.employeeMobileNumber ||
          employeeDetails.employeeMobileNumber,
        id:
          savedEmployee.id ||
          savedEmployee._id ||
          `${employeeDetails.employeeId}-${Date.now()}`,
      };

      setSubmittedDetails((currentDetails) => [
        ...currentDetails,
        normalizedEmployee,
      ]);
      setEmployeeDetails(emptyEmployeeDetails);
      if (currentPage === 1) {
        fetchEmployeeDetails(1);
      } else {
        setCurrentPage(1);
      }
    } catch (error) {
      console.error(
        "Failed to submit employee detalis",
        error.response?.data || error.message,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateEmployee = async (event) => {
    event.preventDefault();

    if (!selectedEmployee?.employeeId) {
      return;
    }

    setIsUpdating(true);

    try {
      await API.put(getEmployeePath(selectedEmployee.employeeId), {
        employeeId: modalEmployeeDetails.employeeId,
        employeeName: modalEmployeeDetails.employeeName,
        employeeEmail: modalEmployeeDetails.employeeEmail,
        employeeMobileNumber: modalEmployeeDetails.employeeMobileNumber,
      });
      handleClosePopup();
      fetchEmployeeDetails(currentPage);
    } catch (error) {
      console.error(
        "Failed to update employee detalis",
        error.response?.data || error.message,
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee?.employeeId) {
      return;
    }

    setIsDeleting(true);

    try {
      await API.delete(getEmployeePath(selectedEmployee.employeeId));
      handleClosePopup();
      fetchEmployeeDetails(currentPage);
    } catch (error) {
      console.error(
        "Failed to delete employee detalis",
        error.response?.data || error.message,
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const paginatedDetails = submittedDetails;

  return (
    <div className="employee-details-page">
      <article className="hr-page-card employee-details-card">
        <div className="hr-page-title">
          <UserRound size={20} />
          <h2>Employee Detalis</h2>
        </div>

        <form className="employee-details-form" onSubmit={handleSubmit}>
          <label className="employee-details-field">
            <span>Employee ID</span>
            <input
              type="text"
              name="employeeId"
              value={employeeDetails.employeeId}
              onChange={handleChange}
              required
            />
          </label>

          <label className="employee-details-field">
            <span>Employee Name</span>
            <input
              type="text"
              name="employeeName"
              value={employeeDetails.employeeName}
              onChange={handleChange}
              required
            />
          </label>

          <label className="employee-details-field">
            <span>Employee Email</span>
            <input
              type="email"
              name="employeeEmail"
              value={employeeDetails.employeeEmail}
              onChange={handleChange}
              required
            />
          </label>

          <label className="employee-details-field">
            <span>Employee Mobile Number</span>
            <input
              type="tel"
              name="employeeMobileNumber"
              value={employeeDetails.employeeMobileNumber}
              onChange={handleChange}
              required
            />
          </label>

          <div className="employee-details-actions">
            <button
              type="submit"
              className="employee-submit-btn"
              disabled={isSubmitting}
            >
              <Send size={16} />
              {isSubmitting ? "Submitting" : "Submit"}
            </button>
          </div>
        </form>
      </article>

      <article className="hr-page-card employee-details-list-card">
        <div className="hr-page-title">
          <UserRound size={20} />
          <h2>Submitted Employee Detalis</h2>
        </div>

        <div className="employee-details-table">
          <div className="employee-details-head">
            <span>Employee ID</span>
            <span>Name</span>
            <span>Email</span>
            <span>Mobile Number</span>
          </div>

          {paginatedDetails.map((item) => (
            <div
              className="employee-details-row"
              key={item.id || item.employeeId}
              role="button"
              tabIndex={0}
              onClick={() => handleEmployeeClick(item)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleEmployeeClick(item);
                }
              }}
            >
              <span>{item.employeeId}</span>
              <span>{item.employeeName}</span>
              <span>{item.employeeEmail}</span>
              <span>{item.employeeMobileNumber}</span>
            </div>
          ))}

          {submittedDetails.length === 0 && (
            <div className="employee-details-empty">No employee detalis submitted.</div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="employee-pagination">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() =>
                setCurrentPage((page) => Math.min(totalPages, page + 1))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </article>

      {selectedEmployee && (
        <div className="employee-popup-overlay" role="presentation" onClick={handleClosePopup}>
          <section
            className="employee-popup"
            role="dialog"
            aria-modal="true"
            aria-label="Employee Detalis"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="employee-popup-close"
              aria-label="Close employee detalis popup"
              onClick={handleClosePopup}
            >
              <X size={20} />
            </button>

            <div className="hr-page-title employee-popup-title">
              <UserRound size={20} />
              <h2>Employee Detalis</h2>
            </div>

            <form className="employee-popup-form" onSubmit={handleUpdateEmployee}>
              <label className="employee-details-field">
                <span>Employee ID</span>
                <input
                  type="text"
                  name="employeeId"
                  value={modalEmployeeDetails.employeeId}
                  onChange={handleModalChange}
                  required
                />
              </label>

              <label className="employee-details-field">
                <span>Employee Name</span>
                <input
                  type="text"
                  name="employeeName"
                  value={modalEmployeeDetails.employeeName}
                  onChange={handleModalChange}
                  required
                />
              </label>

              <label className="employee-details-field">
                <span>Employee Email</span>
                <input
                  type="email"
                  name="employeeEmail"
                  value={modalEmployeeDetails.employeeEmail}
                  onChange={handleModalChange}
                  required
                />
              </label>

              <label className="employee-details-field">
                <span>Employee Mobile Number</span>
                <input
                  type="tel"
                  name="employeeMobileNumber"
                  value={modalEmployeeDetails.employeeMobileNumber}
                  onChange={handleModalChange}
                  required
                />
              </label>

              <div className="employee-popup-actions">
                <button
                  type="button"
                  className="employee-delete-btn"
                  onClick={handleDeleteEmployee}
                  disabled={isDeleting || isUpdating}
                >
                  <Trash2 size={16} />
                  {isDeleting ? "Deleting" : "Delete"}
                </button>
                <button
                  type="submit"
                  className="employee-submit-btn"
                  disabled={isUpdating || isDeleting}
                >
                  <Save size={16} />
                  {isUpdating ? "Updating" : "Update"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
};

export default HREmployeeDetailsPage;
